import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

interface UnsavedChangesBarProps {
  /** When true the bar mounts; when false the component returns `null` (no UI). */
  isDirty: boolean;
  onSave: () => void | Promise<void>;
  onDiscard: () => void;
  /** Disables both buttons while a save is in-flight; Save label flips to "Saving…". */
  isSaving?: boolean;
  /**
   * Extra gate for the Save button (e.g. required-field validation). Defaults to true.
   * The bar still mounts when dirty; the Save button just stays disabled until `canSave` is true.
   */
  canSave?: boolean;
  /** Optional status text shown in place of "You have unsaved changes" (e.g. a blocking validation reason). */
  message?: ReactNode;
  className?: string;
}

/**
 * Floating save/discard dock for settings pages with dirty-state tracking.
 *
 * Returns `null` when the form is clean (no UI footprint). Once a field is edited it appears as
 * a centered, shadowed dock fixed at the bottom of the viewport. We use `position: fixed` rather
 * than `position: sticky` because ancestors of the settings page have `overflow-hidden` and
 * `overflow-auto`, which break sticky binding to the intended scroll container.
 *
 * Pages that mount this bar should add bottom padding (`pb-20`) so the last form section is not
 * hidden behind the bar while it is visible.
 */
export function UnsavedChangesBar({
  isDirty,
  onSave,
  onDiscard,
  isSaving = false,
  canSave = true,
  message,
  className,
}: UnsavedChangesBarProps) {
  if (!isDirty) return null;

  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      className={cn(
        // Anchored to the viewport and horizontally centered via the `left-1/2 + -translate-x-1/2`
        // pattern. `w-[calc(100%-2rem)]` keeps a 16px gutter on small viewports; `max-w-2xl` caps
        // it on wide screens so it reads as a floating dock rather than a window-chrome footer.
        "fixed bottom-4 left-1/2 z-30 w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2",
        "bg-background border-border/80 rounded-lg border shadow-lg",
        "flex flex-row flex-wrap items-center gap-3 px-4 py-3",
        "animate-in slide-in-from-bottom-2 fade-in-0 duration-200",
        className,
      )}
    >
      <span className="text-destructive text-sm font-medium" role="status" aria-live="polite">
        {message ?? "You have unsaved changes"}
      </span>
      <div className="ml-auto flex flex-row items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onDiscard} disabled={isSaving}>
          Discard
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => void onSave()}
          disabled={isSaving || !canSave}
        >
          {isSaving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
